import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Pencil, Trash2, UserX } from 'lucide-react';
import { get, post, patch, del } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Modal from './Modal';

const STATUSES = ['active', 'inactive', 'prospect', 'transferred', 'deceased'];

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-slate-100 text-slate-500',
  prospect: 'bg-amber-100 text-amber-700',
  transferred: 'bg-blue-100 text-blue-700',
  deceased: 'bg-slate-200 text-slate-600',
};

const EMPTY = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  status: 'active',
  household_id: '',
  city: '',
  state: '',
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500';

export default function Members() {
  const { isPastor, isViewer } = useAuth();
  const [members, setMembers] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(null); // member object or {} for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const data = await get('/api/v1/members', { params });
      setMembers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce search
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    get('/api/v1/households')
      .then(setHouseholds)
      .catch(() => {});
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setFormError('');
    setEditing({});
  };

  const openEdit = (m) => {
    setForm({
      first_name: m.first_name || '',
      last_name: m.last_name || '',
      email: m.email || '',
      phone: m.phone || '',
      status: m.status || 'active',
      household_id: m.household_id || '',
      city: m.city || '',
      state: m.state || '',
    });
    setFormError('');
    setEditing(m);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    // Only send non-empty fields; drop empty household_id/email.
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v !== null) payload[k] = v;
    });
    try {
      if (editing.id) {
        await patch(`/api/v1/members/${editing.id}`, payload);
      } else {
        await post('/api/v1/members', payload);
      }
      setEditing(null);
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Mark ${m.first_name} ${m.last_name} inactive?`)) return;
    try {
      await del(`/api/v1/members/${m.id}`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const householdName = (id) =>
    households.find((h) => h.id === id)?.name || '—';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
            Members
          </h2>
          {isViewer && (
            <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5">
              Read-only demo
            </span>
          )}
        </div>
        {!isViewer && (
          <button
            onClick={openNew}
            className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg px-3 py-2 flex items-center gap-1"
          >
            <Plus size={16} /> Add member
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email…"
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">Loading…</div>
        ) : members.length === 0 ? (
          <div className="p-10 text-center text-slate-400 flex flex-col items-center gap-2">
            <UserX size={28} />
            No members found.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-300 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Contact</th>
                <th className="px-4 py-2 font-medium">Household</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-t border-slate-100 dark:border-slate-700">
                  <td className="px-4 py-2 font-medium text-slate-800 dark:text-slate-100">
                    {m.first_name} {m.last_name}
                  </td>
                  <td className="px-4 py-2 text-slate-500 dark:text-slate-400">
                    {m.email || '—'}
                    {m.phone ? <div className="text-xs">{m.phone}</div> : null}
                  </td>
                  <td className="px-4 py-2 text-slate-500 dark:text-slate-400">
                    {householdName(m.household_id)}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                        STATUS_STYLES[m.status] || 'bg-slate-100'
                      }`}
                    >
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end gap-2 text-slate-400">
                      {isViewer ? (
                        <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                      ) : (
                        <>
                          <button
                            onClick={() => openEdit(m)}
                            className="hover:text-brand-600"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          {isPastor && (
                            <button
                              onClick={() => remove(m)}
                              className="hover:text-red-600"
                              title="Mark inactive"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <Modal
          title={editing.id ? 'Edit member' : 'Add member'}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form onSubmit={save} className="space-y-3">
            {formError && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">
                {formError}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name">
                <input
                  required
                  className={inputCls}
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                />
              </Field>
              <Field label="Last name">
                <input
                  required
                  className={inputCls}
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Email">
              <input
                type="email"
                className={inputCls}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone">
                <input
                  className={inputCls}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </Field>
              <Field label="Status">
                <select
                  className={inputCls}
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Household">
              <select
                className={inputCls}
                value={form.household_id}
                onChange={(e) => setForm({ ...form, household_id: e.target.value })}
              >
                <option value="">— None —</option>
                {households.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City">
                <input
                  className={inputCls}
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </Field>
              <Field label="State">
                <input
                  className={inputCls}
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                />
              </Field>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
